import React, { useRef, useState, useEffect } from 'react';
import { aiTemplatesAPI } from '../../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Braces, Link2, Loader2, Save, Eye, Send } from 'lucide-react';
import { toast } from 'sonner';

// Safe merge fields — MUST match backend _KNOWN_MERGE_FIELDS so nothing leaks.
const MERGE_FIELDS = [
  { label: 'First Name', token: 'first_name', sample: 'Sarah' },
  { label: 'Last Name', token: 'last_name', sample: 'Chen' },
  { label: 'Full Name', token: 'name', sample: 'Sarah Chen' },
  { label: 'Company', token: 'company', sample: 'Acme Corp' },
  { label: 'Job Title', token: 'title', sample: 'VP of Sales' },
  { label: 'Email', token: 'email', sample: 'sarah@acme.com' },
  { label: 'Phone', token: 'phone', sample: '+1 555 0100' },
  { label: 'Sender Name', token: 'sender_name', sample: 'Alex Rivera' },
];

const SAMPLE_LEAD = MERGE_FIELDS.reduce((a, f) => ({ ...a, [f.token]: f.sample }), {});

// Client-side mirror of the worker's _render (fill known, strip unknown tokens).
function renderPreview(text) {
  if (!text) return '';
  let out = text;
  MERGE_FIELDS.forEach((f) => {
    const re = new RegExp(`\\{\\{?\\s*${f.token}\\s*\\}?\\}`, 'g');
    out = out.replace(re, SAMPLE_LEAD[f.token]);
  });
  out = out.replace(/\{\{?\s*[a-zA-Z0-9_]+\s*\}?\}/g, ''); // strip anything left
  return out.trim();
}

// Mirror of the backend worker's _text_to_html so the preview matches EXACTLY
// what the recipient sees in their inbox: newlines -> <br>, *,-,• lines -> a
// bullet list, and existing <a href> links preserved.
function textToInboxHtml(text) {
  if (!text) return '';
  const parts = [];
  let inList = false;
  for (const line of text.split('\n')) {
    const s = line.replace(/\s+$/, '');
    const m = s.match(/^\s*[*•-]\s+(.*)$/);
    if (m) {
      if (!inList) { parts.push('<ul style="margin:8px 0;padding-left:22px">'); inList = true; }
      parts.push(`<li>${m[1].trim()}</li>`);
    } else {
      if (inList) { parts.push('</ul>'); inList = false; }
      parts.push(s.trim() === '' ? '<br>' : `${s}<br>`);
    }
  }
  if (inList) parts.push('</ul>');
  return parts.join('');
}

export default function TemplateEditorDialog({ open, onOpenChange, template, onSaved }) {
  const isEdit = !!template;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const activeRef = useRef('body'); // which field last had focus

  useEffect(() => {
    if (open) {
      setName(template?.name || '');
      setDescription(template?.description || '');
      setSubject(template?.content?.subject || '');
      setBody(template?.content?.body || '');
      activeRef.current = 'body';
    }
  }, [open, template]);

  const insertAtCursor = (snippet) => {
    const field = activeRef.current;
    const ref = field === 'subject' ? subjectRef : bodyRef;
    const setter = field === 'subject' ? setSubject : setBody;
    const el = ref.current;
    if (!el) { setter((v) => v + snippet); return; }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const val = el.value;
    const next = val.slice(0, start) + snippet + val.slice(end);
    setter(next);
    // restore cursor just after inserted snippet
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      try { el.setSelectionRange(pos, pos); } catch (_) {}
    });
  };

  const insertVariable = (token) => insertAtCursor(`{{${token}}}`);

  const confirmInsertLink = () => {
    const url = linkUrl.trim();
    if (!url) { toast.error('Enter a URL'); return; }
    const safeUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const text = (linkLabel.trim() || safeUrl);
    activeRef.current = 'body'; // links go in the body
    insertAtCursor(`<a href="${safeUrl}">${text}</a>`);
    setLinkLabel(''); setLinkUrl(''); setLinkOpen(false);
  };

  const handleSendTest = async () => {
    if (!body.trim()) { toast.error('Add some body content first'); return; }
    setSendingTest(true);
    try {
      const res = await aiTemplatesAPI.sendTest({ subject, body });
      const d = res.data || {};
      if (d.sent) toast.success(`Test email sent to ${d.to}`);
      else toast.error(d.error || 'Could not send test email');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSave = async () => {    if (!name.trim()) { toast.error('Give your template a name'); return; }
    if (!body.trim()) { toast.error('Template body cannot be empty'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), subject, body };
      if (isEdit) {
        await aiTemplatesAPI.updateCustom(template.id, payload);
        toast.success('Template updated');
      } else {
        await aiTemplatesAPI.createCustom(payload);
        toast.success('Template created & installed');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const VarDropdown = ({ field }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid={`insert-variable-btn-${field}`}>
          <Braces className="h-3 w-3 mr-1" /> Insert variable
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" data-testid={`insert-variable-menu-${field}`}>
        <DropdownMenuLabel className="text-xs">Lead & sender fields</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {MERGE_FIELDS.map((f) => (
          <DropdownMenuItem key={f.token} onClick={() => insertVariable(f.token)} data-testid={`var-${f.token}`} className="text-xs">
            <span className="flex-1">{f.label}</span>
            <code className="text-[10px] text-muted-foreground">{`{{${f.token}}}`}</code>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle data-testid="template-editor-title">{isEdit ? 'Edit template' : 'Create custom email template'}</DialogTitle>
          <DialogDescription>
            Build reusable emails with safe merge fields. Use “Insert variable” so tokens always fill correctly — never type raw tokens.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Editor */}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Template name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Warm intro — restaurants" data-testid="template-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description (optional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="When to use this template" data-testid="template-description-input" />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-xs">Subject line</Label>
              <div onMouseDown={() => { activeRef.current = 'subject'; }}>
                <VarDropdown field="subject" />
              </div>
            </div>
            <Input
              ref={subjectRef}
              value={subject}
              onFocus={() => { activeRef.current = 'subject'; }}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Quick idea for {{company}}"
              data-testid="template-subject-input"
            />

            <div className="flex items-center justify-between">
              <Label className="text-xs">Body</Label>
              <div className="flex gap-1.5" onMouseDown={() => { activeRef.current = 'body'; }}>
                <VarDropdown field="body" />
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setLinkOpen(true)} data-testid="insert-link-btn">
                  <Link2 className="h-3 w-3 mr-1" /> Insert link
                </Button>
              </div>
            </div>
            <Textarea
              ref={bodyRef}
              value={body}
              onFocus={() => { activeRef.current = 'body'; }}
              onChange={(e) => setBody(e.target.value)}
              placeholder={'Hi {{first_name}},\n\nI noticed {{company}} is ...\n\nBest,\n{{sender_name}}'}
              rows={12}
              className="font-mono text-xs"
              data-testid="template-body-input"
            />
          </div>

          {/* Live inbox-style preview */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1"><Eye className="h-3 w-3" /> Inbox preview (sample lead)</Label>
            <div className="rounded-lg border border-border bg-white dark:bg-zinc-900 shadow-sm overflow-hidden" data-testid="template-preview">
              {/* Inbox message header */}
              <div className="border-b border-border/60 bg-muted/40 px-4 py-2.5 space-y-0.5">
                <div className="font-semibold text-sm text-foreground leading-snug" data-testid="preview-subject">
                  {renderPreview(subject) || <span className="text-muted-foreground italic font-normal">Following up…</span>}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-primary text-[9px] font-bold">A</span>
                  <span><span className="text-foreground/80 font-medium">Alex Rivera</span> &lt;you@company.com&gt;</span>
                  <span className="ml-auto">to Sarah Chen</span>
                </div>
              </div>
              {/* Rendered body — matches the real sent HTML exactly */}
              <div
                className="px-4 py-3.5 text-foreground [&_a]:text-blue-600 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-0.5"
                style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', lineHeight: 1.6 }}
                data-testid="preview-body"
                dangerouslySetInnerHTML={{ __html: textToInboxHtml(renderPreview(body)) || '<span style="opacity:.5">Your email preview appears here…</span>' }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground">This is exactly how the email will render in your recipient's inbox (sample data: Sarah Chen · Acme Corp). Unfilled AI content blanks are auto-written at send time.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="template-editor-cancel">Cancel</Button>
          <Button variant="secondary" onClick={handleSendTest} disabled={sendingTest || !body.trim()} data-testid="template-send-test">
            {sendingTest ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send test to me
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="template-editor-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Insert-link sub-dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-sm" data-testid="insert-link-dialog">
          <DialogHeader>
            <DialogTitle>Insert link</DialogTitle>
            <DialogDescription>Add a clickable link to the email body.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Link text</Label>
              <Input value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} placeholder="Book a call" data-testid="link-label-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">URL</Label>
              <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://calendly.com/you" data-testid="link-url-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)} data-testid="link-cancel">Cancel</Button>
            <Button onClick={confirmInsertLink} data-testid="link-insert-confirm">Insert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
